import { ArrowDownTrayIcon, DocumentTextIcon, LanguageIcon } from '@heroicons/react/24/outline';
import { Button, Group, Modal, rem, SegmentedControl, Select, Stack, Text } from '@mantine/core';
import { saveAs } from 'file-saver';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '../../../hooks/useTranslation';
import { deselectAllIds, getSelectionTier, selectAllIds } from '../../../utils/selectionManager';
import { ExportLimitAlert } from './ExportLimitAlert';
import { ReviewSelectionModal } from './ReviewSelectionModal';

export type ExportLanguage = 'None' | 'English' | 'Polish' | 'French' | 'Norwegian';
export type AnonymizationOption = 'Default' | 'WithoutNames';
export type ExportFormat = 'Docx' | 'Pdf' | 'Europass';

export interface CvExportReviewConfig {
    /** Resolves the ids to preload into the review-and-prune modal. May be async (e.g. a full-list fetch). */
    getIds: () => Promise<number[]> | number[];
    /**
     * Mirrors the currently-reviewed id set back to the caller's own persistent selection, if it has
     * one — fired on every prune/undo during the review session (not just once at "Done"), so a
     * partial review that's abandoned via "Exit" still reflects what was unselected so far.
     */
    onIdsChanged?: (ids: number[]) => void;
}

export interface CvExportOptionsModalProps {
    opened: boolean;
    onClose: () => void;
    title: string;
    /** Count backing the tier warning / hard-block gate before any review has happened. */
    count: number;
    /** Picks ExportLimitAlert's wording ("N CVs selected" vs "N CVs found"). */
    countMode?: 'selected' | 'all-found';
    /**
     * Executes the actual export call and returns the zip Blob. `reviewedIds` is null unless the user
     * completed the optional review-and-prune flow — when null, fall back to your own default id
     * source (current selection, or a dedicated "export by filter/search" endpoint).
     */
    onExport: (
        reviewedIds: number[] | null,
        anonymization: AnonymizationOption,
        language: ExportLanguage,
        format: ExportFormat
    ) => Promise<Blob>;
    /** Used to build the saved filename, e.g. "CVs_Selected" → "CVs_Selected_Docx_WithNames_Original_<timestamp>.zip" */
    fileNamePrefix: string;
    /** Called after a successful export (e.g. to clear selection / exit selection mode). */
    onExported?: () => void;
    /** Enables the "Review list" flow (search/sort/paginate/unselect before exporting). Omit to hide it. */
    review?: CvExportReviewConfig;
}

/**
 * The CV export options popup — format / translation language / anonymization, tier-based warnings,
 * a hard-block safety rail, and an optional review-and-prune step before large exports. Originally
 * built for the CV list's bulk export (`BulkOperationsManager`); extracted here so every CV-export
 * trigger in the app (CV list, Reports) shares the exact same, already-tuned decision logic instead
 * of re-implementing a thinner version per feature.
 */
export const CvExportOptionsModal = ({
    opened,
    onClose,
    title,
    count,
    countMode = 'selected',
    onExport,
    fileNamePrefix,
    onExported,
    review,
}: CvExportOptionsModalProps) => {
    const { t } = useTranslation();
    const [selectedLanguage, setSelectedLanguage] = useState<ExportLanguage>('None');
    const [selectedAnonymization, setSelectedAnonymization] = useState<AnonymizationOption>('Default');
    const [selectedFormat, setSelectedFormat] = useState<ExportFormat>('Docx');
    const [isExporting, setIsExporting] = useState(false);
    const [reviewedIds, setReviewedIds] = useState<number[] | null>(null);
    const [reviewModalOpen, setReviewModalOpen] = useState(false);
    const [reviewWorkingIds, setReviewWorkingIds] = useState<Set<number>>(new Set());
    // The options modal hides itself (without telling the parent to close the whole flow) while the
    // review sub-step is open, then reappears once review finishes or is exited — `opened` (the
    // parent's prop) stays true the entire time; only this internal flag toggles for that handoff.
    const [optionsModalVisible, setOptionsModalVisible] = useState(opened);

    useEffect(() => {
        if (opened) {
            // Fresh invocation — a stale reviewed set from a previous export run must not silently
            // bypass this run's tier gate.
            setOptionsModalVisible(true);
            setReviewedIds(null);
        } else {
            setOptionsModalVisible(false);
            setReviewModalOpen(false);
        }
    }, [opened]);

    const languageOptions: { value: ExportLanguage; label: string }[] = useMemo(() => [
        { value: 'None', label: t('BULK_OPTION_ORIGINAL') },
        { value: 'English', label: t('BULK_OPTION_ENGLISH') },
        { value: 'Polish', label: t('BULK_OPTION_POLISH') },
        { value: 'French', label: t('BULK_OPTION_FRENCH') },
        { value: 'Norwegian', label: t('BULK_OPTION_NORWEGIAN') },
    ], [t]);

    const anonymizationOptions: { value: AnonymizationOption; label: string }[] = useMemo(() => [
        { value: 'Default', label: t('BULK_OPTION_WITH_NAMES') },
        { value: 'WithoutNames', label: t('BULK_OPTION_WITHOUT_NAMES') },
    ], [t]);

    const formatOptions: { value: ExportFormat; label: string }[] = useMemo(() => [
        { value: 'Docx', label: t('BULK_FORMAT_DOCX') },
        { value: 'Pdf', label: t('BULK_FORMAT_PDF') },
        { value: 'Europass', label: t('BULK_FORMAT_EUROPASS') },
    ], [t]);

    const effectiveCount = reviewedIds ? reviewedIds.length : count;
    const tier = getSelectionTier(effectiveCount);
    const requiresMandatoryReview = tier === 'mandatory-review';
    const canExecuteExport = tier === 'hard-block'
        ? false
        : (requiresMandatoryReview ? reviewedIds !== null : true);

    const notifyReviewIdsChanged = (ids: Set<number>) => {
        review?.onIdsChanged?.(Array.from(ids));
    };

    const openReview = async () => {
        if (!review) {
            return;
        }
        const ids = await review.getIds();
        setReviewWorkingIds(new Set(ids));
        setOptionsModalVisible(false);
        setReviewModalOpen(true);
    };

    const handleExport = async () => {
        setIsExporting(true);
        try {
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
            const anonLabel = selectedAnonymization === 'WithoutNames' ? 'Anonymous' : 'WithNames';
            const langLabel = selectedLanguage === 'None' ? 'Original' : selectedLanguage;
            const blob = await onExport(reviewedIds, selectedAnonymization, selectedLanguage, selectedFormat);
            saveAs(blob, `${fileNamePrefix}_${selectedFormat}_${anonLabel}_${langLabel}_${timestamp}.zip`);
            onExported?.();
            onClose();
        } catch (error: any) {
            console.error('Export error:', error);
            alert(error.message || t('BULK_ALERT_EXPORT_FAILED'));
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <>
            <Modal opened={opened && optionsModalVisible} onClose={onClose} title={title} centered size="md">
                <Stack gap="md">
                    <div>
                        <Group gap="xs" mb="xs">
                            <DocumentTextIcon style={{ width: rem(18) }} />
                            <Text fw={500} size="sm">{t('BULK_MODAL_FORMAT')}</Text>
                        </Group>
                        <SegmentedControl
                            fullWidth
                            data={formatOptions}
                            value={selectedFormat}
                            onChange={(value) => setSelectedFormat(value as ExportFormat)}
                            disabled={isExporting}
                        />
                    </div>

                    {selectedFormat === 'Europass' && (
                        <Text size="xs" c="dimmed" p="xs" style={{ background: '#d1ecf1', borderRadius: 4, border: '1px solid #bee5eb' }}>
                            <strong>{t('COMMON_INFO')}:</strong> {t('BULK_MODAL_EUROPASS_INFO')}
                        </Text>
                    )}

                    <div>
                        <Group gap="xs" mb="xs">
                            <LanguageIcon style={{ width: rem(18) }} />
                            <Text fw={500} size="sm">{t('BULK_MODAL_TRANSLATION_LANGUAGE')}</Text>
                        </Group>
                        <Select
                            data={languageOptions}
                            value={selectedLanguage}
                            onChange={(value) => setSelectedLanguage(value as ExportLanguage)}
                            disabled={isExporting}
                        />
                    </div>

                    <div>
                        <Text fw={500} size="sm" mb="xs">{t('BULK_MODAL_ANONYMIZATION')}</Text>
                        <Select
                            data={anonymizationOptions}
                            value={selectedAnonymization}
                            onChange={(value) => setSelectedAnonymization(value as AnonymizationOption)}
                            disabled={isExporting || selectedFormat === 'Europass'}
                        />
                        {selectedFormat === 'Europass' && (
                            <Text size="xs" c="dimmed" mt="xs">{t('BULK_MODAL_ANONYMIZATION_UNAVAILABLE')}</Text>
                        )}
                    </div>

                    <ExportLimitAlert
                        selectedCount={effectiveCount}
                        mode={countMode}
                        onReviewList={review ? openReview : undefined}
                    />

                    {tier === 'hard-block' && (
                        <Text size="sm" c="red.7">
                            {t('BULK_MODAL_HARD_BLOCK')}
                        </Text>
                    )}

                    {tier === 'hard-block' ? (
                        <Button fullWidth size="md" variant="default" onClick={onClose}>
                            {t('BULK_MODAL_OK')}
                        </Button>
                    ) : (
                        <Button
                            fullWidth
                            size="md"
                            color="brand"
                            leftSection={<ArrowDownTrayIcon style={{ width: rem(18) }} />}
                            onClick={handleExport}
                            loading={isExporting}
                            disabled={isExporting || !canExecuteExport}
                        >
                            {t('BULK_MODAL_EXPORT_ZIP')}
                        </Button>
                    )}
                </Stack>
            </Modal>

            {review && (
                <ReviewSelectionModal
                    opened={reviewModalOpen}
                    selectedIds={reviewWorkingIds}
                    onClose={() => {
                        setReviewModalOpen(false);
                        setOptionsModalVisible(true);
                    }}
                    onDeselectByIds={(ids) => {
                        setReviewWorkingIds((prev) => {
                            const next = deselectAllIds(prev, ids);
                            notifyReviewIdsChanged(next);
                            return next;
                        });
                    }}
                    onReselectByIds={(ids) => {
                        setReviewWorkingIds((prev) => {
                            const next = selectAllIds(prev, ids);
                            notifyReviewIdsChanged(next);
                            return next;
                        });
                    }}
                    onClearAll={() => {
                        setReviewWorkingIds(new Set());
                        notifyReviewIdsChanged(new Set());
                    }}
                    onReviewCompleted={(finalIds) => {
                        // ReviewSelectionModal's "Done" always calls onClose right after this, which
                        // handles reopening the options modal — nothing else to do here.
                        setReviewedIds(finalIds);
                        notifyReviewIdsChanged(new Set(finalIds));
                    }}
                />
            )}
        </>
    );
};
